import React, { useState, useCallback, useMemo } from 'react';
import cn from 'classnames';
import { useMutation } from '@tanstack/react-query';
import debounce from 'lodash/debounce';
import { useForm } from 'react-hook-form';
import { InputField, CalloutBox, CalloutBoxThemes } from 'ht-styleguide';

// Apis
import { user } from '@api/apis';

// Utils
import { Logger } from '@utils/logger';
import { noop } from '@utils/event';

// Constants
import { AUTO_SUBMIT_DEBOUNCE_TIMEOUT_MS } from '@scenes/LoginPage/Modal/constants';
import { YupSchemaResolver } from '@constants/validations';

// Styles
import styles from './loginPasswordless.module.scss';

/**
 * Handles email input and account checking for the initial step of passwordless authentication.
 */

type TLoginFormPasswordlessProps = {
  onSuccess: BaseAnyFunction;
  onError?: BaseAnyFunction;
  className?: string;
};

export const LoginFormPasswordless = ({ onSuccess = noop, onError = noop, className }: TLoginFormPasswordlessProps) => {
  const [showCallout, setShowCallout] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    formState: { errors },
  } = useForm<{ email_autoOff: string }>({
    ...YupSchemaResolver(['email_autoOff'], undefined, {
      email: '',
    }),
  });

  const { mutate } = useMutation({
    mutationFn: async ({ email }: { email: string }) => {
      const response = await user.checkAccount({ email });
      if (response.err) {
        const is404 = response.err.response.status === 404;
        const errorMessage = is404 ? response.data?.errors?.[0] || `${email} was not found` : 'Something went wrong';
        throw new Error(errorMessage);
      }
      return response.data;
    },
    onSuccess: (data, { email }) => {
      const { phone_last_four: phoneLastFour = '', user_initials: userInitials = '' } = data;
      onSuccess({
        phoneLastFour,
        userInitials,
        email,
      });
    },
    onError: error => {
      setShowCallout(true);
      onError(error);
      Logger('LoginFormPasswordless')(error);
    },
  });

  // mutate from tanstack query v5 is stable (bound to the MutationObserver ref)
  const onSubmit = useCallback(
    (data: { email_autoOff: string }) => {
      mutate({ email: data.email_autoOff });
    },
    [mutate],
  );

  const debouncedSubmit = useMemo(() => debounce(handleSubmit(onSubmit), AUTO_SUBMIT_DEBOUNCE_TIMEOUT_MS), [handleSubmit, onSubmit]);

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const trimmedEmail = e.target.value.replace(/\s/g, '');
      setShowCallout(false);
      setError('email_autoOff', { type: 'manual', message: '' });
      setValue('email_autoOff', trimmedEmail, { shouldDirty: true });

      if (trimmedEmail) {
        debouncedSubmit();
      }
    },
    [setShowCallout, setError, setValue, debouncedSubmit],
  );

  return (
    <div className={className}>
      <p className={cn('p1 n900', styles.subheader)}>Enter your email to access your HelloTech account</p>
      <form autoComplete="off">
        <InputField
          id="passwordless-email"
          type="text"
          label="Email address"
          error={errors.email_autoOff?.message}
          {...register('email_autoOff')}
          onChange={handleEmailChange}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
          autoComplete="off"
        />
      </form>

      {showCallout && (
        <CalloutBox
          theme={CalloutBoxThemes.DEFAULT}
          header="We couldn't find an account matching that email address"
          text="Want to create an account? Simply book your first service!"
          className="marginTop-small2"
        />
      )}
    </div>
  );
};
